import React from "react";
import NumericLabel from "react-pretty-numbers";

import { Popover, OverlayTrigger } from "react-bootstrap";
import Toggle from "react-toggle";

import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";

import Select from "react-select";
import Slider, { Range, Handle } from "rc-slider";

import { AutoSizer } from "react-virtualized";

import { LineChart, ResponsiveContainer, ReferenceLine, Line, XAxis, YAxis, Tooltip } from "recharts";

import CapSlider from "components/Project/CapSlider";
import RunFrontier from "containers/FrontierSliders/RunFrontier";
import { SubgroupTableItemGraph } from "components/Monitoring/Subgroups";

import {
  USER_GROUP_DATA_KEYS,
  getUserTargetingPlotKey,
  getGroupColor
} from "containers/Next/Monitoring/NextMonitoringReducer";

import { constructPlotFromData, GRAPH_MODES } from "containers/Monitoring/reducer";

import styles from "./style.module.css";

import numeral from "numeral";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";

import { Button, getRange, getPrettyNumber } from "aible";
import { LineInput } from "components/Inputs/LineInput";

const CustomToolTip = (props) => {
  const { active, payload, setupLabels, isCumulative, groupIndices } = props;

  const currPayload = payload && payload[0] && payload[0].payload;

  if (!active || !currPayload) {
    return null;
  }

  const profitKey = `profit${isCumulative ? "_cumulative" : ""}`;

  return (
    <div className={styles.cTooltip} style={{ fontSize: "14px" }}>
      <div className={styles.cHeader}>{currPayload.date}</div>
      <table className={styles.fairnessTable}>
        <tbody>
          {groupIndices?.map((index) => {
            const valueKey = getUserTargetingPlotKey(index, profitKey);
            const labelKey = getUserTargetingPlotKey(index, "name");
            if (isNaN(currPayload[valueKey])) {
              return <React.Fragment key={"tooltip_" + index} />;
            }
            return (
              <tr key={"tooltip_" + index}>
                <td className={styles.tLabel}>
                  <div className={styles.tLegendIndicator} style={{ background: getGroupColor(index) }} />
                  <span>{currPayload[labelKey]}</span>
                </td>
                <td>
                  <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", width: "100%" }}>
                    {(currPayload[valueKey] < 0 ? "-" : "") + setupLabels.unitType + Math.abs(currPayload[valueKey])}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default class UserTargeting extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isSaving: false,
      isDeleting: false,
      graphMode: GRAPH_MODES.DAILY
    };
  }

  renderResultsGraph() {
    const {
      userData,
      userTargetingPlot,
      setupLabels,
      userTargeting,
      isOverallSegment,
      hasUserComplianceData,
      compliancePlot,
      businessPredictionName,
      adjustAdvancedOptions,
      isNext
    } = this.props;

    const { groupA, groupB, graphMode } = this.state;

    const graphModeOptions = [
      { label: "Cumulative", value: GRAPH_MODES.CUMULATIVE },
      { label: "Daily", value: GRAPH_MODES.DAILY }
    ];

    const groupIndices = userTargeting?.map((u, index) => index);

    return (
      <div className={styles.lineGraphWrapper} style={{ marginTop: "20px" }}>
        <div className={styles.lineGraphTitle}>
          <div className={styles.lineGraphTitleSelect}>
            <Select
              value={graphModeOptions.find((elem) => elem.value === graphMode)}
              onChange={(s) => this.setState({ graphMode: s?.value })}
              options={graphModeOptions}
              className={styles.outcomePicker}
            />
          </div>
          <div className={styles.lineGraphTitleText}>{`${setupLabels.profitLabel}`}</div>
        </div>
        <div className={styles.lineGraphBody}>
          <AutoSizer>
            {({ width, height }) => (
              <LineChart
                width={width}
                height={height}
                data={userTargetingPlot}
                margin={{ top: 10, right: 0, left: 20, bottom: 0 }}
              >
                <Tooltip
                  content={
                    <CustomToolTip
                      setupLabels={setupLabels}
                      groupIndices={groupIndices}
                      isCumulative={graphMode === GRAPH_MODES.CUMULATIVE}
                    />
                  }
                  itemStyle={{ fontSize: "14px" }}
                  contentStyle={{ fontSize: "14px" }}
                  wrapperStyle={{ background: "#FFFFFF" }}
                />
                <XAxis dataKey={"date"} domain={userTargetingPlot?.length ? undefined : ["", ""]} />
                {groupIndices?.map((index) => {
                  const dataKey = getUserTargetingPlotKey(
                    index,
                    `profit${graphMode === GRAPH_MODES.CUMULATIVE ? "_cumulative" : ""}`
                  );
                  return (
                    <Line
                      key={"userTarget_group_" + index}
                      dataKey={dataKey}
                      stroke={getGroupColor(index)}
                      strokeWidth={3}
                      dot={false}
                    />
                  );
                })}
                <ReferenceLine y={0} strokeDasharray="3 3" />
                <YAxis tickFormatter={(tick) => getPrettyNumber(tick)} />
              </LineChart>
            )}
          </AutoSizer>
        </div>
      </div>
    );
  }

  renderGroup(index) {
    const {
      userTargeting,
      deployedScenarioIndex,
      updateUserGroupValue,
      users,
      allScenarios,
      setupLabels,
      userData,
      runId,
      id
    } = this.props;

    const userOptions = users?.map((uData) => {
      return {
        label: uData?.fullname,
        value: uData?.id
      };
    });

    const scenarioOptions = allScenarios.map((scen) => {
      const label = scen?.originalScenario?.name;
      const value = scen?.index;
      return {
        value,
        label
      };
    });

    scenarioOptions.unshift({
      label: "Custom Aggressiveness Setting",
      value: -10
    });

    const currentUserGroup = userTargeting?.[index] || {};

    const updateCurrUserGroup = (key, value) => updateUserGroupValue(index, key, value);

    const currentUsers = currentUserGroup.users || [];

    const otherUsers = [];
    userTargeting?.forEach((uGroup) => {
      const ugUsers = uGroup?.users;
      ugUsers?.forEach((user) => {
        if (!otherUsers.includes(user)) {
          otherUsers.push(user);
        }
      });
    });

    const currentName = currentUserGroup.name === undefined ? "Team " + (index + 1) : currentUserGroup.name;

    const currentScenarioIndex =
      currentUserGroup.scenarioIndex === undefined ? deployedScenarioIndex : currentUserGroup.scenarioIndex;

    let currentThreshold = currentUserGroup.threshold;

    return (
      <React.Fragment>
        <div className={styles.groupCreationItem + " " + styles.row} style={{ marginBottom: "10px" }}>
          <div
            className={styles.tLegendIndicator}
            style={{ background: getGroupColor(index), height: "20px", width: "20px", marginRight: "6px" }}
          />
          <LineInput
            label="Group Name"
            isMediumBox
            value={currentName}
            onChange={(val) => updateCurrUserGroup("name", val)}
          />
        </div>
        <div className={styles.groupCreationItem}>
          <Select
            placeholder={"Add Team Members..."}
            options={userOptions.filter((option) => !otherUsers.includes(option.value))}
            value={userOptions.filter((option) => currentUsers.includes(option.value))}
            isMulti
            onChange={(val) => updateCurrUserGroup("users", val ? val.map((v) => v.value) : [])}
            styles={{ menu: (provided) => ({ ...provided, zIndex: 1000 }) }}
          />
        </div>
        <div
          className={styles.groupCreationItem}
          style={currentScenarioIndex < 0 ? { marginBottom: "0px" } : undefined}
        >
          <div className={styles.groupCreationLabel}>{currentName + " Specialized Scenario"}</div>
          <Select
            options={scenarioOptions}
            value={scenarioOptions.find((option) => Number(option.value) === Number(currentScenarioIndex))}
            onChange={(val) => {
              updateCurrUserGroup("scenarioIndex", val?.value);
            }}
            styles={{ menu: (provided) => ({ ...provided, zIndex: 1000 }) }}
          />
        </div>
        {currentScenarioIndex < 0 && (
          <div className={styles.groupCreationItem}>
            <div className={styles.groupCreationLabel} style={{ marginBottom: "-10px" }}>
              {"AI Aggressiveness"}
            </div>
            <RunFrontier
              id={runId}
              projectId={id}
              onChange={(e) => updateCurrUserGroup("threshold", e / 100)}
              value={currentThreshold !== undefined ? currentThreshold * 100 : undefined}
              isNext
              // isFeedback
              hideButton
            />
          </div>
        )}
        <div className={styles.groupCreationItem}>
          <table className={styles.ufTable}>
            <tbody>
              <tr className={styles.ufHeader}>
                <td className={styles.ufHeaderLabel}>{"User"}</td>
                <td className={styles.ufHeaderLabel + " " + styles.ufLeftDivide}>{"Total Impact"}</td>
                <td className={styles.ufHeaderLabel}>{setupLabels.notPursuedTransactionLabel + " Count"}</td>
              </tr>
              {currentUsers &&
                currentUsers.map((userId) => (
                  <UserPreference
                    {...this.props}
                    userId={Number(userId)}
                    currUserData={userData[userId]}
                    key={"user " + userId}
                  />
                ))}
            </tbody>
          </table>
        </div>
      </React.Fragment>
    );
  }

  renderGroupCreation() {
    const {
      userData,
      runId,
      id,
      setupLabels,
      isOverallSegment,
      hasUserComplianceData,
      compliancePlot,
      businessPredictionName,
      adjustAdvancedOptions,
      isNext,
      allScenarios,
      users,
      userTargeting,
      updateUserTargetingByIndex
    } = this.props;

    const groupIndices = [0, 1];

    return (
      <div className={styles.groupCreationWrapper}>
        {groupIndices.map((gIndex, i) => {
          return (
            <div className={styles.groupCreationCol} key={"user_targeting_" + gIndex}>
              {this.renderGroup(gIndex)}
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    const {
      userData,
      setupLabels,
      isOverallSegment,
      hasUserComplianceData,
      compliancePlot,
      businessPredictionName,
      adjustAdvancedOptions,
      isNext,
      deleteUserTargeting,
      saveUserTargeting
    } = this.props;

    return (
      <div className={styles.userFeedbackWrapper}>
        {/* <div className={styles.userFeedbackTitle} style={isNext ? {fontSize: "16px", borderBottomColor: "transparent"} : undefined}>
          {"Champion/Challenger Targeting"}
        </div> */}
        {this.renderResultsGraph()}

        {this.renderGroupCreation()}
        <div className={styles.userTargetingFooter}>
          <Button
            style={{ marginRight: "20px" }}
            type={"secondary"}
            disabled={this.state.isDeleting || this.state.isSaving}
            onClick={() => {
              this.setState({ isDeleting: true });
              return deleteUserTargeting().then(() => {
                this.setState({ isDeleting: false });
              });
            }}
          >
            {this.state.isDeleting ? (
              <LoadingIndicator
                style={{
                  width: "20px",
                  height: "18px",
                  transform: "scale(0.30)",
                  margin: "-5px 5px 5px -5px"
                }}
              />
            ) : (
              "Delete Groups"
            )}
          </Button>
          <Button
            type={"primary"}
            disabled={this.state.isDeleting || this.state.isSaving}
            onClick={() => {
              this.setState({ isSaving: true });
              return saveUserTargeting().then(() => {
                this.setState({ isSaving: false });
              });
            }}
          >
            {this.state.isSaving ? (
              <LoadingIndicator
                style={{
                  width: "20px",
                  height: "18px",
                  transform: "scale(0.30)",
                  margin: "-5px 5px 5px -5px"
                }}
              />
            ) : (
              "Save Groups"
            )}
          </Button>
        </div>
      </div>
    );
  }
}

class UserPreference extends React.PureComponent {
  getCurrSegmentData() {
    const { currUserData, segmentId } = this.props;
    const currSegmentData = currUserData && currUserData.data && currUserData.data[segmentId];
    return currSegmentData;
  }

  getImpact() {
    const { currUserData, segmentId } = this.props;
    const currSegmentScenarioData =
      currUserData && currUserData.data && currUserData.data[segmentId] && currUserData.data[segmentId].scenarios;
    const totalImpact =
      currSegmentScenarioData &&
      currSegmentScenarioData.reduce((acc, cData) => {
        const currProfit =
          cData && cData.selectedScenario && isFinite(cData.selectedScenario.profit)
            ? cData.selectedScenario.profit
            : 0;
        return acc + currProfit;
      }, 0);
    return totalImpact ? totalImpact : 0;
  }

  render() {
    const {
      currUserData,
      id,
      runId,
      userId,
      segmentId,
      setupLabels,
      isContinuous,
      isOverallSegment,
      hasUserComplianceData,
      showUserAnalytics
    } = this.props;

    if (!currUserData) {
      return <React.Fragment />;
    }

    const user = currUserData.user || {};

    const segmentData = this.getCurrSegmentData();

    return (
      <tr className={styles.ufRow}>
        <td className={styles.ufItem + " " + styles.ufTitle}>{user.fullname}</td>
        <td className={styles.ufItem + " " + styles.ufGraph + " " + styles.ufLeftDivide}>
          <SubgroupTableItemGraph
            data={segmentData && segmentData.data}
            dataKey={isContinuous ? "cost" : "impact"}
            unitType={setupLabels.unitType}
            isSmall
            isCumulative
          />
        </td>
        <td className={styles.ufItem + " " + styles.ufGraph}>
          <SubgroupTableItemGraph
            data={segmentData && segmentData.data}
            dataKey={"count"}
            isWholeNumber
            isSmall
            isCumulative
          />
        </td>
      </tr>
    );
  }
}
